import type { MutableRefObject } from 'react';

import { ACADEMIC_PALETTE_PRESETS } from '@/app/lib/academicPalette';
import type { RutgersCourse } from '@/app/types/interfaces';

import { populatedProfileFixture } from './profile.fixtures';
import type { ProfileWorkspaceViewProps } from './ProfileWorkspaceView';
import type { ProfileTabId } from './profileViewModel';

const noop = () => undefined;
const noopAsync = async () => undefined;

export const completedCourseFixture = {
  id: '01:198:112:01',
  baseId: '01198112',
  courseName: 'Data Structures',
  fullTitle: 'DATA STRUCTURES',
  department: '198',
  class_number: '112',
  credits: '4',
  campus: 'Busch',
  meetingTimes: [],
  instructors: ['Scarlet Instructor'],
  section: '01',
  subjectNotes: null,
  coreRequirements: [],
} satisfies RutgersCourse;

export function createProfileWorkspaceFixture(activeTab: ProfileTabId = 'profile'): ProfileWorkspaceViewProps {
  return {
    summary: { ...populatedProfileFixture, activeTab },
    activeTab,
    displayName: 'Scarlet Student',
    email: 'student@example.invalid',
    avatarUrl: null,
    subscriptionLabel: 'Free plan',
    completedCourseCount: 1,
    memoryCount: 1,
    onRetry: noop,
    onSignOut: noop,
    onTabChange: noop,
    onTabKeyDown: noop,
    tabRefs: { current: [] } as MutableRefObject<Array<HTMLButtonElement | null>>,
    account: {
      displayName: 'Scarlet Student',
      email: 'student@example.invalid',
      overviewCards: [
        { label: 'Saved schedules', value: '1', detail: 'A primary schedule is set.' },
        { label: 'Friends', value: '3', detail: 'Accepted connections.' },
        { label: 'Degree plans', value: '1', detail: 'One planning roadmap.' },
      ],
      academicData: { year: 'Junior', majors: ['Computer Science'], school: 'School of Arts and Sciences', referral_source: 'Friend' },
      academicErrors: {},
      isEditingAcademic: false,
      academicSaving: false,
      degrees: [],
      majorSearch: '',
      onMajorSearchChange: noop,
      onAcademicDataChange: noop,
      onToggleAcademicEdit: noop,
      onCancelAcademicEdit: noop,
      onSaveAcademic: noopAsync,
      feedbackMode: 'enhanced',
      onFeedbackModeChange: noop,
      palettePreferences: { coursePaletteId: ACADEMIC_PALETTE_PRESETS[0].id, programPaletteId: ACADEMIC_PALETTE_PRESETS[1].id },
      palettePresets: ACADEMIC_PALETTE_PRESETS.slice(0, 2),
      explorerPreferences: { defaultView: 'grid', departmentColorsEnabled: true },
      onExplorerDefaultViewChange: noop,
      onExplorerDepartmentColorsChange: noop,
      onCoursePaletteChange: noop,
      onProgramPaletteChange: noop,
      onDeleteCustomPalette: noop,
      onResetPalettes: noop,
      showCustomPaletteBuilder: false,
      onToggleCustomPaletteBuilder: noop,
      customPaletteName: '',
      customPaletteDescription: '',
      customPaletteError: null,
      customCourseSwatches: [],
      customProgramSwatches: [],
      onCustomPaletteNameChange: noop,
      onCustomPaletteDescriptionChange: noop,
      onCustomSwatchChange: noop,
      onSaveCustomPalette: noop,
      onResetCustomPaletteBuilder: noop,
      onDeleteAccount: noop,
    },
    previousClasses: {
      completedCourses: [completedCourseFixture],
      grades: [{ code: completedCourseFixture.baseId, grade: 'A' }],
      lastSaved: '2026-08-27T14:00:00.000Z',
      saving: false,
      saveError: null,
      onReloadRecord: noop,
      addDialogOpen: false,
      searchQuery: '',
      searchResults: [completedCourseFixture],
      onSearchQueryChange: noop,
      onOpenAddDialog: noop,
      onCloseAddDialog: noop,
      onAddCourse: noopAsync,
      onRemoveCourse: noop,
      onGradeChange: noopAsync,
      onOpenPrerequisiteChecker: noop,
    },
    connections: {
      acceptedCount: 3,
      incomingRequestCount: 1,
      sentRequestCount: 2,
      publicScheduleCount: 1,
    },
    schedules: {
      schedules: [{ schedule_id: 'schedule-fixture', name: 'Fall plan', description: 'Primary planning schedule', is_public: true, updated_at: '2026-08-27T14:00:00.000Z' }],
      primaryScheduleId: 'schedule-fixture',
      onOpenScheduling: noop,
      onOpenSchedule: noop,
      onDeleteSchedule: noop,
    },
    courseLists: {
      lists: [{ id: 1, name: 'CS electives', description: 'Backup course options', updated_at: '2026-08-27T14:00:00.000Z' }],
      onOpenCourseLists: noop,
      onOpenList: noop,
      onShareList: noop,
      onDeleteList: noop,
    },
    degreePlans: {
      plans: [{ id: 1, name: 'Computer Science plan', is_shareable: true, created_at: '2026-08-27T14:00:00.000Z' }],
      retrieved: true,
      onRetry: noop,
      onOpenPlanner: noop,
      onOpenPlan: noop,
      onDeletePlan: noop,
    },
    billing: {
      state: 'ready',
      accessStatus: 'free',
      accessUntil: null,
      subscriptionStatus: null,
      cancelAtPeriodEnd: false,
      currentPeriodEnd: null,
      hasBillingAccount: false,
      checkoutState: null,
      actionBusy: null,
      error: null,
      onStartCheckout: noop,
      onOpenPortal: noop,
      onRetry: noop,
    },
    professionalContent: <div>Professional profile fixture</div>,
    memories: {
      memories: [{ id: 'memory-fixture', memory_key: 'Preferred campus', memory_value: 'Busch', created_at: '2026-08-27T14:00:00.000Z', updated_at: '2026-08-27T14:00:00.000Z' }],
      loading: false,
      onOpenAssistant: noop,
      onDeleteMemory: noop,
    },
  };
}
